<template>
  <main class="about-page">
    <!-- Per-page SEO: unique title + meta description (Requirement 10.1). -->
    <SeoHead :seo="content.seo" />

    <!-- Overview / mission (Requirement 5.1) -->
    <section class="about-overview" aria-labelledby="about-overview-heading">
      <v-container>
        <v-row align="center" class="about-overview__row">
          <v-col cols="12" md="6">
            <ScrollReveal>
              <h1 id="about-overview-heading" class="about-heading">
                {{ content.overview.heading }}
              </h1>
              <p class="about-mission">{{ content.overview.mission }}</p>
              <!--
                A paragraph is either a plain string or an array of segments,
                where a segment carrying an `href` becomes a link. Each segment is
                interpolated, so the copy stays escaped by Vue rather than being
                injected as HTML.
              -->
              <p
                v-for="(paragraph, index) in content.overview.body"
                :key="index"
                class="about-body"
              >
                <template v-if="Array.isArray(paragraph)"
                  ><template
                    v-for="(segment, segmentIndex) in paragraph"
                    :key="segmentIndex"
                    ><a
                      v-if="segment.href"
                      :href="segment.href"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="about-body__link"
                      >{{ segment.text }}</a
                    ><template v-else>{{ segment.text }}</template></template
                  ></template
                ><template v-else>{{ paragraph }}</template>
              </p>
            </ScrollReveal>
          </v-col>
          <v-col cols="12" md="6">
            <ScrollReveal>
              <img
                :src="content.overview.image.src"
                :alt="content.overview.image.alt"
                class="about-overview__image"
                loading="lazy"
              />
            </ScrollReveal>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Values / differentiators (Requirement 5.2) -->
    <section class="about-values" aria-labelledby="about-values-heading">
      <v-container>
        <ScrollReveal>
          <h2 id="about-values-heading" class="about-section-heading">
            {{ content.values.heading }}
          </h2>
        </ScrollReveal>
        <v-row>
          <v-col
            v-for="(value, index) in content.values.items"
            :key="index"
            cols="12"
            md="4"
          >
            <ScrollReveal :delay="index * 100">
              <v-card class="about-value-card" variant="flat" height="100%">
                <v-card-item>
                  <h3 class="about-value-title">{{ value.title }}</h3>
                  <p class="about-value-body">{{ value.body }}</p>
                </v-card-item>
              </v-card>
            </ScrollReveal>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Team composition and delivery capacity -->
    <section
      v-if="content.team"
      class="about-team"
      aria-labelledby="about-team-heading"
    >
      <v-container>
        <ScrollReveal>
          <h2 id="about-team-heading" class="about-section-heading">
            {{ content.team.heading }}
          </h2>
          <p class="about-team__intro">{{ content.team.intro }}</p>
        </ScrollReveal>

        <ScrollReveal>
          <ul class="about-team__roles">
            <li
              v-for="role in content.team.roles"
              :key="role"
              class="about-team__role"
            >{{ role }}</li>
          </ul>
        </ScrollReveal>

        <ScrollReveal>
          <p class="about-team__capacity">{{ content.team.capacity }}</p>
        </ScrollReveal>
      </v-container>
    </section>

    <!-- Credibility: experience / accreditations / milestones (Requirement 5.3) -->
    <section class="about-credibility" aria-labelledby="about-credibility-heading">
      <v-container>
        <ScrollReveal>
          <h2 id="about-credibility-heading" class="about-section-heading">
            {{ content.credibility.heading }}
          </h2>
          <p class="about-credibility__intro">{{ content.credibility.intro }}</p>
        </ScrollReveal>
        <v-row>
          <v-col
            v-for="(milestone, index) in content.credibility.milestones"
            :key="index"
            cols="12"
            md="4"
          >
            <ScrollReveal :delay="index * 100">
              <div class="about-milestone">
                <h3 class="about-milestone__title">{{ milestone.title }}</h3>
                <p class="about-milestone__body">{{ milestone.body }}</p>
              </div>
            </ScrollReveal>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Closing CTA to Contact (Requirement 5.4) -->
    <section class="about-cta" aria-labelledby="about-cta-heading">
      <v-container>
        <ScrollReveal>
          <div class="about-cta__inner">
            <h2 id="about-cta-heading" class="about-section-heading">
              {{ content.closingCta.heading }}
            </h2>
            <p class="about-cta__body">{{ content.closingCta.body }}</p>
            <CtaButton
              :to="content.closingCta.cta.to"
              :label="content.closingCta.cta.label"
            />
          </div>
        </ScrollReveal>
      </v-container>
    </section>
  </main>
</template>

<script>
// AboutPage — the About Us page (Requirement 5).
//
// Renders the company overview/mission (5.1), values/differentiators (5.2),
// credibility content (5.3), and a closing CTA to the Contact page (5.4).
// All editorial copy and imagery come from the structured content module
// `client/src/content/about.js` so copy changes need no redesign
// (Requirements 11.2, 10.1).
//
// Uses the Options API to match the project's `plugin:vue/essential` ESLint
// preset.
import about from '@/content/about'
import SeoHead from '@/components/SeoHead.vue'
import ScrollReveal from '@/components/ScrollReveal.vue'
import CtaButton from '@/components/CtaButton.vue'

export default {
  name: 'AboutPage',
  components: {
    SeoHead,
    ScrollReveal,
    CtaButton,
  },
  data () {
    return {
      // About_Page content (copy, imagery, SEO) from the content module.
      content: about,
    }
  },
}
</script>

<style lang="scss" scoped>
.about-heading {
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1.15;
  color: #41281b;
  margin-bottom: 16px;
}

.about-section-heading {
  font-size: 2rem;
  font-weight: 800;
  color: #41281b;
  margin-bottom: 16px;
}

.about-mission {
  font-size: 1.15rem;
  font-weight: 600;
  color: #4a4a4a;
  margin-bottom: 16px;
}

.about-body {
  font-size: 1rem;
  color: #4a4a4a;
  line-height: 1.7;
  margin-bottom: 12px;
}

/*
  Inline link within body copy. Underlined rather than colour-only, so it is
  distinguishable without relying on colour perception.
*/
.about-body__link {
  color: #e7511e;
  text-decoration: underline;
}

.about-body__link:hover,
.about-body__link:focus {
  text-decoration: none;
}

.about-overview {
  padding: 64px 0;
}

.about-overview__row {
  row-gap: 24px;
}

.about-overview__image {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 12px;
}

.about-values {
  padding: 64px 0;
  background-color: #f7f5f4;
}

.about-value-card {
  background-color: #fff;
  border-radius: 12px;
  height: 100%;
}

.about-value-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #41281b;
  margin-bottom: 8px;
}

.about-value-body {
  font-size: 0.95rem;
  color: #4a4a4a;
  line-height: 1.6;
}

.about-team {
  padding: 64px 0;
}

.about-team__intro {
  font-size: 1.05rem;
  color: #4a4a4a;
  line-height: 1.7;
  max-width: 720px;
  margin-bottom: 24px;
}

.about-team__roles {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
}

.about-team__role {
  padding: 8px 14px;
  background-color: #f7f5f4;
  border: 1px solid rgba(65, 40, 27, 0.12);
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #41281b;
}

.about-team__capacity {
  font-size: 1.05rem;
  color: #4a4a4a;
  line-height: 1.75;
  max-width: 760px;
}

.about-credibility {
  padding: 64px 0;
}

.about-credibility__intro {
  font-size: 1.05rem;
  color: #4a4a4a;
  margin-bottom: 24px;
}

.about-milestone {
  padding: 16px 0;
}

.about-milestone__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #e7511e;
  margin-bottom: 8px;
}

.about-milestone__body {
  font-size: 0.95rem;
  color: #4a4a4a;
  line-height: 1.6;
}

.about-cta {
  padding: 64px 0;
  background-color: #41281b;
}

.about-cta__inner {
  text-align: center;
}

.about-cta .about-section-heading {
  color: #fff;
}

.about-cta__body {
  font-size: 1.1rem;
  color: #e6ded9;
  margin-bottom: 24px;
}
</style>
